import React from 'react';
import { Form, InputNumber, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import type { ResourcePolicy } from '@/api/mcp-appointments';
import { DEFAULT_DURATION_MINUTES } from '../constants';

interface DurationInputProps {
  resourcePolicy: ResourcePolicy | undefined;
  duration: number;
  onDurationChange: (value: number | null) => void;
}

export const DurationInput: React.FC<DurationInputProps> = ({
  resourcePolicy,
  duration,
  onDurationChange
}) => {
  const { t } = useTranslation('manage');
  const isPolicyDurationLocked = !!resourcePolicy?.slotDurationMinutes;

  return (
    <Form.Item
      label={t('appointmentComponents.create.duration.label')}
      name="duration"
      normalize={(value) => {
        if (value == null || value === '') return resourcePolicy?.slotDurationMinutes ?? DEFAULT_DURATION_MINUTES;
        const n = Number(value);
        if (Number.isNaN(n)) return resourcePolicy?.slotDurationMinutes ?? DEFAULT_DURATION_MINUTES;
        return Math.min(480, Math.max(5, Math.floor(n)));
      }}
      tooltip={
        resourcePolicy?.slotDurationMinutes
          ? t('appointmentComponents.create.duration.tooltipWithPolicy', { minutes: resourcePolicy.slotDurationMinutes })
          : t('appointmentComponents.create.duration.tooltip')
      }
      rules={[
        { required: true, message: t('appointmentComponents.create.duration.required') },
        {
          validator: (_, value) => {
            if (value === null || value === undefined || value === '') {
              return Promise.reject(new Error(t('appointmentComponents.create.duration.required')));
            }
            const numValue = Number(value);
            if (isNaN(numValue)) {
              return Promise.reject(new Error(t('appointmentComponents.create.duration.mustBeNumber')));
            }
            if (numValue < 5) {
              return Promise.reject(new Error(t('appointmentComponents.create.duration.minDuration')));
            }
            if (numValue > 480) {
              return Promise.reject(new Error(t('appointmentComponents.create.duration.maxDuration')));
            }
            // Validate against policy: duration must be a multiple of slotDurationMinutes (backend allows 30, 60, 90 for 30-min slot)
            if (resourcePolicy?.slotDurationMinutes) {
              if (numValue % resourcePolicy.slotDurationMinutes !== 0) {
                return Promise.reject(
                  new Error(t('appointmentComponents.create.duration.policyDurationMultiple', { minutes: resourcePolicy.slotDurationMinutes }))
                );
              }
            }
            return Promise.resolve();
          }
        }
      ]}
    >
      <Space.Compact style={{ width: '100%' }}>
        <InputNumber<number>
          min={5}
          max={480}
          step={resourcePolicy?.slotDurationMinutes || 5}
          precision={0}
          size="middle"
          style={{ width: '100%' }}
          placeholder={resourcePolicy?.slotDurationMinutes?.toString() || "30"}
          controls={false}
          disabled={isPolicyDurationLocked}
          readOnly={isPolicyDurationLocked}
          formatter={(value) => (value != null && String(value) !== '' ? String(Math.floor(Number(value))) : '')}
          parser={(displayValue) => {
            const parsed = displayValue?.replace(/\D/g, '') ?? '';
            return parsed === '' ? (null as unknown as number) : Number(parsed);
          }}
          onKeyDown={(e) => {
            const key = e.key;
            if (key === 'Backspace' || key === 'Delete' || key === 'Tab' || key === 'ArrowLeft' || key === 'ArrowRight' || key === 'Home' || key === 'End') return;
            if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(key.toLowerCase())) return;
            if (!/^[0-9]$/.test(key)) e.preventDefault();
          }}
          onChange={(value) => {
            const safe = value != null && !Number.isNaN(Number(value))
              ? Math.min(480, Math.max(5, Math.floor(Number(value))))
              : (resourcePolicy?.slotDurationMinutes ?? DEFAULT_DURATION_MINUTES);
            onDurationChange(safe);
          }}
        />
        <Space.Addon>{t('appointmentComponents.create.duration.minutes')}</Space.Addon>
      </Space.Compact>
    </Form.Item>
  );
};
